import {AttrsSchema, Component, jsx} from 'panel';
import {VNode} from 'snabbdom';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {ChartsContextRegistry} from 'common/charts/contexts/registry';
import {ChartType} from 'common/report/constants';
import {RetentionTrendsMetricChartEl} from 'common/report/retention/charts/retention-trends-metric-chart';
import {RetentionTrendsChartEl} from 'common/report/retention/charts/retention-trends-chart';
import {flattenQueryResponse} from 'common/report/retention/util/request';
import {getFormattedRowData, hasRetentionData} from 'common/report/retention/util/parsing';
import {RetentionCurveChartEl} from 'common/report/retention/charts/retention-curve-chart';
// import {Popover} from 'common/charts/visualization/line-chart/types';
import {CurrentProject} from 'common/report/model-resources/projects';
import {RetentionQuery} from 'common/report/retention/models/retention-query';
import {SuccessResponse} from 'common/report/retention/models/types';
import {ReportLayoutManager} from 'common/report/contexts/report-layout';
// import {FormattedRow} from 'common/report/retention/util/types';
import {frequencyLineChartDescription, retentionLineChartDescription} from 'common/report/retention/util';
import {OTHER_SEGMENT_VALUE} from 'common/report/insights/util/constants';
import {RetentionBookmarkType} from 'common/report/retention/charts/types';
import {RetentionBookmarkController} from 'common/report/retention/bookmark-controller/retention';
import {AGGREGATED_ROW_NAME, RetentionTrackingEvents} from 'common/report/retention/util/constants';

import {handlePopoverAction, RetentionPopoverActionParams, trackPopover, ViewUsersType} from '../popover';
import css from './index.inline.styl';

type RetentionQueryAndResults = {
  query: RetentionQuery;
  results: SuccessResponse;
};

type RetentionChartDisplayJsx = {
  props: {
    project: null | CurrentProject;
    queryAndResults: RetentionQueryAndResults;
  };
  attrs: {
    'is-download-mode': boolean;
    'show-resizing-layout': boolean;
  };
  on?: EmptyObj;
};

export class RetentionChartDisplay extends Component<
  EmptyObj,
  RetentionChartDisplayJsx['attrs'],
  unknown,
  unknown,
  ChartsContextRegistry
> {
  static get attrsSchema(): AttrsSchema<RetentionChartDisplayJsx['attrs']> {
    return {
      'is-download-mode': `boolean`,
      'show-resizing-layout': `boolean`,
    };
  }

  get config() {
    return {
      css,
      useShadowDom: true,
      template: () => this.renderTemplate(),
      contexts: [`reportLayoutManager` as const],
      defaultState: {},
    };
  }

  private _project: null | CurrentProject = null;
  get project() {
    return this._project;
  }
  set project(project: null | CurrentProject) {
    this._project = project;
    this.update();
  }

  private query: RetentionQuery = null;
  private results: SuccessResponse;

  set queryAndResults(value: RetentionQueryAndResults) {
    this.query = value.query;
    this.results = value.results;
    this.update();
  }

  popooverCallback = (segmentName: string) => {
    if (segmentName === OTHER_SEGMENT_VALUE) {
      return [];
    }
    return [
      {
        label: `View retained users`,
        icon: `team`,
        value: ViewUsersType.Retain,
      },
      {
        label: `View dropped off users`,
        icon: `team`,
        value: ViewUsersType.DropOff,
      },
    ];
  };

  private renderTemplate(): VNode {
    return (
      <div sel='.root' class={{'resizing-layout': this.attr(`show-resizing-layout`)}}>
        {this.renderChart()}
      </div>
    );
  }

  private renderChart(): null | VNode {
    if (this.query.chartType === ChartType.RetentionTrendMetric) {
      return this.renderMetricChart();
    }

    if (this.query.chartType === ChartType.RetentionTrend) {
      return this.renderTrendChart();
    }

    const {metadata, data} = flattenQueryResponse(this.results, this.query.groupBy);
    const tableData = getFormattedRowData(
      data,
      metadata.incomplete_buckets,
      this.query.groupBy,
      this.project?.timezone,
    );
    if (this.query.isRetentionCurveBasedChart() && hasRetentionData(tableData)) {
      return this.renderCurveChart();
    }
    return null;
  }

  private renderMetricChart(): VNode {
    return (
      <div sel='.chart-widget.metric-chart-container'>
        <RetentionTrendsMetricChartEl
          sel='.retention-trends-metric-chart'
          class={{'fixed-size-chart': this.reportLayoutManager.hasFixedSizeChart()}}
          params={{
            projectTimezone: this.project?.timezone,
            paramsAndResults: {
              params: this.query.generateBookmark(),
              results: this.results,
            },
          }}
        />
      </div>
    );
  }

  private renderTrendChart(): VNode {
    return (
      <div
        sel='.chart-widget.trends-chart-container'
        className={{'fixed-size-chart': this.reportLayoutManager.hasFixedSizeChart()}}
      >
        <RetentionTrendsChartEl
          attrs={{
            'project-timezone': this.project?.timezone,
            'utc-offset': this.project?.utcOffset,
            'show-annotations': !this.attr(`is-download-mode`),
          }}
          props={{
            paramsAndResults: {
              params: this.query.generateBookmark(),
              results: this.results,
            },
            popoverCallback: this.popooverCallback,
          }}
          on={{
            popoverAction: (ev) => {
              const {action, selectedRow, colIdx, rowIdx, color} = ev.detail;
              const bookmarkController = new RetentionBookmarkController(this.query.generateBookmark(), this.results);
              const retentionType = bookmarkController.getRetentionType();
              const isAggregated =
                bookmarkController.getSegments().length > 0 ||
                selectedRow.raw_head === AGGREGATED_ROW_NAME(retentionType);
              const cell = selectedRow.data[colIdx];
              const popover = bookmarkController.createPopover({
                projectTimezone: this.project?.timezone,
                colIdx,
                rowIdx,
                groupedRowIdx: colIdx,
                absoluteValue: cell.formatted_value,
                absoluteDropOffValue: cell.absolute_drop_off_value,
                cohortSize: cell.cohort_size,
                completeDate: cell.complete_date,
                enteredValue: cell.entered_value,
                enteredDate: cell.date,
                incomplete: cell.incomplete,
                isAggregated,
              });
              handlePopoverAction({
                action,
                color,
                popover,
              });
              trackPopover({
                eventName: RetentionTrackingEvents.PopoverViewUsers,
                component: this,
                query: this.query,
                popover,
              });
            },
          }}
        />
      </div>
    );
  }

  private renderCurveChart(): VNode {
    const curveChartNote = this.query.isAddiction()
      ? frequencyLineChartDescription(this.query)
      : retentionLineChartDescription(this.query);

    return (
      <div sel='.curve-chart-container' class={{'fixed-size-chart': this.reportLayoutManager.hasFixedSizeChart()}}>
        <RetentionCurveChartEl
          sel='.chart-widget.retention-curve-chart'
          class={{'fixed-size-chart': this.reportLayoutManager.hasFixedSizeChart()}}
          attrs={{
            'project-timezone': this.project?.timezone,
            'utc-offset': this.project?.utcOffset,
            'show-popover': true,
          }}
          props={{
            bookmarkData: {
              bookmark: this.query.generateBookmark(),
              queryResults: this.results,
              type: RetentionBookmarkType.RETENTION,
            },
            popoverAction: (params: RetentionPopoverActionParams) => {
              const {action, color, popover} = params;
              handlePopoverAction({
                action,
                color,
                popover,
              });
              trackPopover({
                eventName: RetentionTrackingEvents.PopoverViewUsers,
                component: this,
                query: this.query,
                popover,
              });
            },
          }}
        />
        <div sel='.retention-line-chart-footnote'>{curveChartNote}</div>
      </div>
    );
  }

  private get reportLayoutManager(): ReportLayoutManager {
    return this.getContext(`reportLayoutManager`);
  }
}

export const RetentionChartDisplayEl = defineAndMakeJsxFactory<RetentionChartDisplayJsx>(
  `retention-chart-display`,
  RetentionChartDisplay,
);
export const RetentionChartDisplayReact = panel2react<RetentionChartDisplayJsx>(
  `retention-chart-display`,
  RetentionChartDisplay,
);
